import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { subscribeOptionsChanged, notifyOptionsChanged } from "@/lib/optionsBus";

/**
 * TestingDetailsChecklist — checkbox grid for testing_details with ability to add custom items inline.
 * Props: values: string[], onChange(values), testId
 */
export default function TestingDetailsChecklist({ values = [], onChange, testId }) {
  const [options, setOptions] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [custom, setCustom] = useState("");

  const fetchOptions = async () => {
    try {
      const { data } = await api.get(`/options/testing_details`);
      setOptions(data || []);
    } catch (e) { console.error("Failed to fetch testing_details options:", e); }
  };

  useEffect(() => {
    fetchOptions();
    const unsub = subscribeOptionsChanged("testing_details", fetchOptions);
    return unsub;
  }, []);

  const toggle = (opt) => {
    if (values.includes(opt)) onChange(values.filter((v) => v !== opt));
    else onChange([...values, opt]);
  };

  const addCustom = async () => {
    const v = custom.trim();
    if (!v) return;
    if (!options.includes(v)) {
      try { await api.post(`/options/testing_details`, { value: v }); } catch (e) { console.error("Failed to save testing_details option:", e); }
      setOptions((p) => (p.includes(v) ? p : [...p, v].sort()));
      notifyOptionsChanged("testing_details");
    }
    if (!values.includes(v)) onChange([...values, v]);
    setCustom("");
    setShowAdd(false);
  };

  // Show selected items that may not be in current options list (e.g., already-saved custom)
  const allOpts = Array.from(new Set([...options, ...values])).sort();

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border border-[#E2E8F0] rounded-md bg-white">
        {allOpts.map((opt) => {
          const checked = values.includes(opt);
          return (
            <label
              key={opt}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-sm cursor-pointer transition-colors ${
                checked
                  ? "border-[#0055FF] bg-[#EFF6FF] text-[#0A0A0A]"
                  : "border-[#E2E8F0] hover:border-[#94A3B8] text-[#475569]"
              }`}
              data-testid={`${testId}-opt-${opt}`}
            >
              <Checkbox
                checked={checked}
                onCheckedChange={() => toggle(opt)}
                className="data-[state=checked]:bg-[#0055FF] data-[state=checked]:border-[#0055FF]"
              />
              <span className="select-none">{opt}</span>
            </label>
          );
        })}
      </div>
      {showAdd ? (
        <div className="flex gap-2">
          <Input
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
            placeholder="Custom testing note (e.g., HDMI port loose)"
            autoFocus
            data-testid={`${testId}-custom-input`}
          />
          <Button onClick={addCustom} className="bg-[#0055FF] hover:bg-[#0040C1] text-white" data-testid={`${testId}-custom-save`}>Add</Button>
          <Button variant="outline" onClick={() => { setShowAdd(false); setCustom(""); }}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setShowAdd(true)} className="gap-1 h-8" data-testid={`${testId}-add-custom`}>
          <Plus className="w-3.5 h-3.5" /> Add custom note
        </Button>
      )}
    </div>
  );
}
